import { useMemo, useEffect, useState, useCallback } from 'react';
import { Box, Button, Snackbar, Slide, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  getFollowingFeed,
  getHomeFeed,
  getNewsFeed,
} from '../../../utils/apis/memeverse';
import {
  CommentType,
  PaginatedPosts,
  PostType,
  FlattenedFeedType,
} from '../memeverse_types';
import FeedPage from './FeedPage';
import VirtualizedFeed, { VirtualizedFeedHandle } from './VirtualizedFeed';
import DetectBottomScrollDiv from '../../journaling/DetectBottomScrollDiv';
import Lottie from 'lottie-react';
import LoadingFace from '../../../assets/animations/LoadingFace.json';
import { useAuth } from '../../../hooks/useAuth';
import { useAskWebsockets } from '../hooks/useAskWebsockets';
import { flattenCommentsList } from '../helper';
import { useTheme } from '@mui/material';
import { useRefreshActions } from './RefreshStatesContext';
import { useNavigate } from 'react-router-dom';

const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';
const NEW_POSTS_COUNT = 1;
export const POST_PAGE_SIZE = 10;

export type FeedType = 'news' | 'forYou' | 'following';

interface FeedComponentProps {
  feedType: FeedType;
  isActive: boolean;
  onReplyClick: (item: PostType | CommentType) => void;
  onTabChange?: (event: any, newValue: number) => void;
  scrollBoxRef?: React.RefObject<HTMLElement>;
  onEmptyStateChange?: (isEmpty: boolean) => void;
  feedRef?: React.RefObject<VirtualizedFeedHandle>;
}

const FeedComponent = ({
  feedType,
  isActive,
  onReplyClick,
  onTabChange,
  scrollBoxRef,
  onEmptyStateChange,
  feedRef,
}: FeedComponentProps) => {
  const { authUser, authLoading } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [showNewPostSnackbar, setShowNewPostSnackbar] =
    useState<boolean>(false);
  const { triggerRefresh } = useRefreshActions();

  // Configure query key and fetch function based on feed type
  const getQueryConfig = () => {
    switch (feedType) {
      case 'news':
        return {
          queryKey: ['memesNews', authUser?.uid],
          fetchFn: (pageParam: number) =>
            getNewsFeed(authUser?.uid, POST_PAGE_SIZE, 5, pageParam),
          websocketKey: ['memesNews', authUser?.uid],
        };
      case 'forYou':
        return {
          queryKey: ['memesCommunity', authUser?.uid, authLoading],
          fetchFn: (pageParam: number) =>
            getHomeFeed(authUser?.uid, POST_PAGE_SIZE, 5, pageParam, 'recent'),
          websocketKey: ['memesCommunity', authUser?.uid, authLoading],
          websocketCallback: (postId: string) => {
            // navigate(`/ask/post/${postId}`) // navigate to post details page
            if (onTabChange && scrollBoxRef) {
              onTabChange(null, 1);
              scrollBoxRef.current?.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth' as ScrollBehavior,
              });
            }
          },
        };
      case 'following':
        return {
          queryKey: ['memesFromFollowing', authUser?.uid],
          fetchFn: (pageParam: number) =>
            getFollowingFeed(authUser?.uid, POST_PAGE_SIZE, pageParam),
          websocketKey: ['memesFromFollowing', authUser?.uid],
        };
      default:
        throw new Error(`Invalid feed type: ${feedType}`);
    }
  };

  const config = getQueryConfig();

  const handleJumpToTop = () => {
    scrollBoxRef?.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    setShowNewPostSnackbar(false);
    resetNewPostCount();
  };

  const handleCloseSnackbar = () => {
    setShowNewPostSnackbar(false);
    resetNewPostCount();
  };

  function SlideTransition(props: any) {
    return <Slide {...props} direction='up' />;
  }

  // Setup infinite query
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: config.queryKey,
      queryFn: async ({ pageParam }) => {
        if (authLoading || (feedType === 'following' && !authUser?.uid)) {
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
          } as FlattenedFeedType;
        }

        try {
          const resp = await config.fetchFn(pageParam);
          if (!resp.ok) {
            return {
              pagination: {},
              posts: {},
              comments: {},
              orderedPosts: [],
            } as FlattenedFeedType;
          }

          const data: PaginatedPosts = await resp.json();
          if (enableDevTools) console.log(`${feedType} feed data: `, data);

          const posts = {} as Record<string, PostType>;
          const comments = {} as Record<string, CommentType>;
          const orderedPosts = [] as string[];

          for (const post of data.posts) {
            post.page_no = pageParam;
            posts[post.docId] = post;
            orderedPosts.push(post.docId);

            if (post.comments_list) {
              post.flat_comments_list = flattenCommentsList(
                comments,
                post.comments_list,
                pageParam
              );
            } else {
              post.flat_comments_list = [];
            }
          }

          return {
            pagination: data.pagination,
            posts,
            comments,
            orderedPosts,
          } as FlattenedFeedType;
        } catch (err) {
          console.log(err);
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
          } as FlattenedFeedType;
        }
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination.has_next) {
          return undefined;
        }
        return lastPage.pagination.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        if (!firstPage.pagination.has_next) {
          return undefined;
        }
        return firstPage.pagination.next_page;
      },
      enabled: !authLoading,
    });

  const handleDocumentUpdate = useCallback(
    (documentId: string, type: 'post' | 'comment') => {
      if (!data?.pages) return;
      let documentExists = false;

      for (const page of data.pages) {
        if (type === 'post' && page.posts[documentId]) {
          documentExists = true;
          break;
        } else if (type === 'comment' && page.comments[documentId]) {
          documentExists = true;
          break;
        }
      }
      if (documentExists) {
        triggerRefresh(documentId);
      }
    },
    [data?.pages]
  );

  const { newPostCount, resetNewPostCount } = useAskWebsockets(
    config.websocketKey as any,
    config.websocketCallback,
    handleDocumentUpdate
  );

  // Show snackbar when we reach N new posts
  useEffect(() => {
    if (isActive && newPostCount.current >= NEW_POSTS_COUNT) {
      setShowNewPostSnackbar(true);
    }
  }, [newPostCount.current, isActive]);

  // Calculate total post count for empty state detection
  const totalPostCount = useMemo(() => {
    if (!data?.pages) return 0;
    return data.pages.reduce(
      (total, page) => total + (page.orderedPosts?.length || 0),
      0
    );
  }, [data?.pages]);

  // Notify parent component about empty state for following feed
  useEffect(() => {
    if (feedType === 'following' && onEmptyStateChange) {
      const isEmpty = !isFetching && totalPostCount === 0;
      onEmptyStateChange(isEmpty);
    }
  }, [feedType, onEmptyStateChange, isFetching, totalPostCount]);

  // Flatten posts for virtualization
  const flattenedPosts = useMemo(() => {
    if (!data?.pages) return [];
    const posts: PostType[] = [];
    data.pages.forEach((page, pageIdx) => {
      page.orderedPosts?.forEach((postId, postIdx) => {
        const post = page.posts[postId];
        if (post) posts.push(post);
        // Inject Ad every 5 posts (matching FeedPage logic)
        const globalIndex = pageIdx * POST_PAGE_SIZE + postIdx;
        if ((globalIndex + 1) % 5 === 0) {
          // We create a dummy post object for the Ad
          posts.push({ docId: `ad-${globalIndex}` } as any);
        }
      });
    });
    return posts;
  }, [data?.pages]);

  const allComments = useMemo(() => {
    if (!data?.pages) return {};
    return data.pages.reduce(
      (acc, page) => ({ ...acc, ...page.comments }),
      {} as Record<string, CommentType>
    );
  }, [data?.pages]);

  const loadNextPage = () => {
    if (enableDevTools) console.log(`Loading next page for ${feedType}`);
    if (!isFetching && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  // Handle loading and empty states
  if (isLoading || (isFetching && flattenedPosts.length === 0)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          minHeight: '400px',
        }}
      >
        <Lottie
          animationData={LoadingFace}
          loop={true}
          style={{ width: '150px', height: '150px' }}
        />
        <Typography
          variant="body1"
          sx={{ mt: 2, color: theme.palette.text.secondary }}
        >
          Loading your feed...
        </Typography>
      </Box>
    );
  }

  if (!isFetching && flattenedPosts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignOrigin: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          gap: 2,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="text.primary">
          No posts found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {feedType === 'news'
            ? "We couldn't find any headlines at the moment. Please check back later!"
            : feedType === 'following'
              ? "Your following feed is empty. Follow some creators to see their posts here!"
              : "The community feed is currently quiet. Why not start the conversation?"}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(0)}
          sx={{ mt: 1 }}
        >
          Refresh Feed
        </Button>
      </Box>
    );
  }

  // Only render if this feed is active to avoid unnecessary renders
  if (!isActive) {
    return null;
  }

  // Show loading state while fetching initial data
  const isInitialLoading =
    isFetching && !isFetchingNextPage && flattenedPosts.length === 0;

  return (
    <>
      <Box sx={{ height: '100%' }}>
        {isInitialLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Lottie
              animationData={LoadingFace}
              loop
              style={{ width: 120, height: 120 }}
            />
          </Box>
        ) : (
          <VirtualizedFeed
            ref={feedRef}
            posts={flattenedPosts}
            allComments={allComments}
            onReplyClick={onReplyClick}
            onLoadMore={loadNextPage}
            hasMore={!!hasNextPage}
            isNewsFeed={feedType === 'news'}
          />
        )}
      </Box>

      {isActive && (
        <Snackbar
          open={showNewPostSnackbar}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          message={`${newPostCount.current} new post${
            newPostCount.current > 1 ? 's' : ''
          } available`}
          action={
            <Button
              color='inherit'
              variant='outlined'
              size='small'
              onClick={handleJumpToTop}
              style={{
                borderColor: theme.palette.primary.contrastText,
                color: theme.palette.primary.contrastText,
              }}
            >
              JUMP TO TOP
            </Button>
          }
          sx={{ position: 'absolute' }}
          slots={{ transition: SlideTransition }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      )}
    </>
  );
};

export default FeedComponent;
